import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Col, Row, message} from "antd";
import Formatter from "../../Data/Domain/Formatter";
import { Title } from "../../Utils/UI/Text";
import {
  getProjectsCountSelector,
  getProjectsSelector,
} from "../Infrastructure/Projects.reducer";
import { getProjects, getProject } from "../Application/Projects.business";
import NewProject from "./NewProject";
import ExportButtonElement from "../../Layout/DesignSystem/ExportButton/Button";
import { ProjectOutlinedIcon } from "../../Utils/UI/Text";
import "./NewProject.css";
import InterfaceTable from "../../Table/InterfaceTable";

export default function ProjectsBoard() {
  const navigate = useNavigate();
  const { id } = useParams();
  const project = useSelector(getProjectsSelector);

  const count = useSelector(getProjectsCountSelector);
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({
    page: 0,
    limit: 10,
    field_sort: "_id",
  });
  const [filters, setFilters] = useState("false");

  const goToProject = (projectId, type) => {
    navigate(`/projects/${projectId}`);
  };

  useEffect(() => {
    fetchData({
      page: 0,
      limit: 10,
      field_sort: "_id",
    });
    if (id) {
      getProject(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async (params = {}) => {
    try {
      setLoading(true);
      const finalParams = {
        limit: values.limit,
        page: values.page,
        field_sort: values.field_sort,
        ...params,
      };
      await getProjects(finalParams);
      setLoading(false);
    } catch (error) {
      message.error("Error al cargar los datos");
      setLoading(false);
    }
  };

  const handleRequest = (newPagination = {}, newFilters = {}, sorter = {}) => {
    const { current = 1, pageSize = values.limit } = newPagination;

    const updatedValues = {
      page: current - 1,
      limit: pageSize,
      field_sort: "_id",
    };
    setValues(updatedValues);

    const cleanParams = Formatter.cleanParamsFromTableToAPI({
      ...newFilters,
      limit: updatedValues.limit,
      page: updatedValues.page,
      field_sort: "_id",
      dates: [],
    });

    setFilters(newFilters);
    fetchData(cleanParams);
  };

  const columns = [
    {
      title: "Polygon",
      dataIndex: ["info", "en", "polygon"],
      key: "polygon",
    },
    {
      title: "Assignee",
      dataIndex: ["info", "en", "company_name"],
      key: "company_name",
    },
    {
      title: "Total Land (ha)",
      dataIndex: ["info", "en", "total_land_ha"],
      key: "total_land_ha",
    },
    {
      title: "Productive Area (ha)",
      dataIndex: ["info", "en", "productive_area_ha"],
      key: "productive_area_ha",
    },
    {
      title: "Coordinates UTM X",
      dataIndex: ["latitude"],
      key: "coordinates_utm_x",
    },
    {
      title: "Coordinates UTM Y",
      dataIndex: ["longitude"],
      key: "coordinates_utm_y",
    },
    {
      title: "Management Plan",
      dataIndex: ["info", "en", "management_plan_adjunto"],
      key: "management_plan",
    },
    {
      title: "Agreements",
      dataIndex: ["info", "en", "agreements_adjunto"],
      key: "agreements",
    },
    {
      title: "Removal/Avoidance",
      dataIndex: ["info", "en", "removalavoidance"],
      key: "removalavoidance",
    },
    {
      title: "Type",
      dataIndex: ["info", "en", "type"],
      key: "type",
    },
    {
      title: "Certifier",
      dataIndex: ["info", "en", "certifier"],
      key: "certifier",
    },
    {
      title: "Methodology",
      dataIndex: ["info", "en", "methodology"],
      key: "methodology",
    },
    {
      title: "Biomass",
      dataIndex: ["info", "en", "biomass"],
      key: "biomass",
    },
    {
      title: "CO2",
      dataIndex: ["info", "en", "co2"],
      key: "co2",
    },
    {
      title: "Tillage Credits (TON/HA/YEAR)",
      dataIndex: ["info", "en", "tillage_credits_tonhayear"],
      key: "tillage_credits_tonhayear",
    },
    {
      title: "Fuel use & infrastructure (CO2)",
      dataIndex: ["info", "en", "fuel_use_and_infrastructure_co2_tonhaao"],
      key: "fuel_use_and_infrastructure_co2_tonhaao",
    },
    {
      title: "Total Avoided Emissions (CO2)",
      dataIndex: ["info", "en", "total_avoided_emissions_c02_tonhaao"],
      key: "total_avoided_emissions_c02_tonhaao",
    },
    {
      title: "Leakage (TON/HA/AÑO)",
      dataIndex: ["info", "en", "leakage_tonhaao"],
      key: "leakage_tonhaao",
    },
    {
      title: "Uncertain (TON/HA/AÑO)",
      dataIndex: ["info", "en", "uncertain_tonhaao"],
      key: "uncertain_tonhaao",
    },
    {
      title: "Credit before Buffer discount",
      dataIndex: ["info", "en", "credit_before_buffer_discount_tonhaao"],
      key: "credit_before_buffer_discount_tonhaao",
    },
    {
      title: "BUFFER 20%",
      dataIndex: ["info", "en", "buffer_20_tonhaao"],
      key: "buffer_20_tonhaao",
    },
    {
      title: "Final Credits",
      dataIndex: ["info", "en", "final_credits_tonhaao"],
      key: "final_credits_tonhaao",
    },
    {
      title: "Final Annual Credits",
      dataIndex: ["info", "en", "final_annual_credits"],
      key: "final_annual_credits",
    },
    {
      title: "Credits in 20",
      dataIndex: ["info", "en", "credits_in_20"],
      key: "credits_in_20",
    },
    {
      title: "% Contribution to Overall Project",
      dataIndex: ["info", "en", "_contribution_to_overall_project"],
      key: "_contribution_to_overall_project",
    },
    {
      title: "Species Count",
      dataIndex: ["info", "en", "species_count"],
      key: "species_count",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <ProjectOutlinedIcon
          onClick={() => goToProject(record.id)}
          TooltipProjectOutlined="Detalle del Proyecto"
        />
      ),
    },
  ];

  return (
    <>
      <main>
        <Title
          children="My Projects"
          childrenTwo={<NewProject />}
          childrenThree={
            <ExportButtonElement
              type={"projects"}
              text={"Exportar"}
              filter={filters}
            />
          }
        />

        <Row>
          <Col span={24}>
            <InterfaceTable
              rowKey="id"
              pagination={{
                pageSize: values.limit,
                total: count,
              }}
              onChange={handleRequest}
              data={project}
              columns={columns}
              loading={loading}
            />
          </Col>
        </Row>
      </main>
    </>
  );
}
